// App Identity Utilities
// -----------------------
// Derives a display abbreviation and a badge color from an app name.
// Both are deterministic — the same name always produces the same result.
// Used when creating a new web application so every app has a distinct,
// consistent visual identity without requiring manual input from the user.

// Curated palette of visually distinct, professional colors.
// Chosen to work well on white backgrounds with white text in a badge.
const APP_COLOR_PALETTE = [
  "#6366f1", // indigo
  "#0ea5e9", // sky
  "#10b981", // emerald
  "#f59e0b", // amber
  "#ef4444", // red
  "#8b5cf6", // violet
  "#ec4899", // pink
  "#14b8a6", // teal
  "#f97316", // orange
  "#64748b", // slate
];

// Derive App Abbreviation
// ------------------------
// Multi-word names: take the first letter of each word, up to 3 characters.
//   "My Web App"         → "MWA"
//   "Acme Portal"        → "AP"
//   "Customer Dashboard" → "CD"
// Single-word names: take the first 3 characters.
//   "Shopfront"          → "SHO"
//   "API"                → "API"
//   "Go"                 → "GO"
//
export function deriveAppAbbrev(name: string): string {
  if (!name?.trim()) return "APP";

  const words = name.trim().split(/\s+/).filter(Boolean);

  if (words.length > 1) {
    return words
      .slice(0, 3)
      .map((word) => word[0].toUpperCase())
      .join("");
  }

  return words[0].substring(0, 3).toUpperCase();
}

// Derive App Color
// -----------------
// Hashes the app name to a stable index into APP_COLOR_PALETTE.
// The same name always produces the same color, but different names
// spread across the palette to maximize visual distinction.
//
export function deriveAppColor(name: string): string {
  if (!name?.trim()) return APP_COLOR_PALETTE[0];

  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = (hash * 31 + name.charCodeAt(i)) >>> 0;
  }

  return APP_COLOR_PALETTE[hash % APP_COLOR_PALETTE.length];
}
